import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';

const TEAM_COLORS: Record<string, string> = {
  BLUE: '#3b82f6',
  RED: '#ef4444',
  GREEN: '#22c55e',
  YELLOW: '#eab308',
};

interface HeroHPDisplayProps {
  mode: {
    type: 'hero-hp';
    heroName: string;
    teamColor: string;
    currentHP: number;
    maxHP: number;
  };
  socket: Socket | null;
}

export default function HeroHPDisplay({ mode, socket }: HeroHPDisplayProps) {
  const [currentHP, setCurrentHP] = useState(mode.currentHP);
  const [maxHP] = useState(mode.maxHP);
  const [isShaking, setIsShaking] = useState(false);
  const [isGlowing, setIsGlowing] = useState(false);
  const [floatingText, setFloatingText] = useState<{
    value: number;
    isHeal: boolean;
    key: number;
    animate: boolean;
  } | null>(null);

  const barColor = TEAM_COLORS[mode.teamColor] || '#60a5fa';
  const hpPercent = maxHP > 0 ? (currentHP / maxHP) * 100 : 0;

  useEffect(() => {
    if (!socket) return;

    const handleHPUpdate = (data: {
      damage: number;
      heal: number;
      currentHP: number;
      maxHP: number;
    }) => {
      const isDamage = data.damage > 0;
      const value = isDamage ? data.damage : data.heal;

      // Animazione
      if (isDamage) {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      } else {
        setIsGlowing(true);
        setTimeout(() => setIsGlowing(false), 800);
      }

      // Floating text
      const key = Date.now() + Math.random();
      setFloatingText({ value, isHeal: !isDamage, key, animate: false });
      requestAnimationFrame(() => {
        setFloatingText((prev) =>
          prev && prev.key === key ? { ...prev, animate: true } : prev
        );
      });
      setTimeout(() => setFloatingText(null), 1100);

      // Aggiorna HP
      setCurrentHP(data.currentHP);
    };

    socket.on('hero:hp-update', handleHPUpdate);
    return () => {
      socket.off('hero:hp-update', handleHPUpdate);
    };
  }, [socket]);

  return (
    <div className="h-screen w-screen bg-black flex items-center justify-center">
      <style>{`
        @keyframes hero-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .hero-shake { animation: hero-shake 0.5s ease-in-out; }
        @keyframes hero-glow {
          0% { box-shadow: 0 0 0px rgba(74,222,128,0); }
          50% { box-shadow: 0 0 40px rgba(74,222,128,0.6), 0 0 80px rgba(74,222,128,0.3); }
          100% { box-shadow: 0 0 0px rgba(74,222,128,0); }
        }
        .hero-glow { animation: hero-glow 0.8s ease-in-out; }
        @keyframes hero-enter {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div
        className={`relative w-3/5 ${isShaking ? 'hero-shake' : ''} ${isGlowing ? 'hero-glow' : ''}`}
        style={{ animation: 'hero-enter 0.4s ease-out' }}
      >
        {/* Floating text */}
        {floatingText && (
          <span
            className={`absolute left-1/2 -translate-x-1/2 font-bold pointer-events-none z-10 ${
              floatingText.isHeal ? 'text-green-400' : 'text-red-500'
            }`}
            style={{
              fontSize: '4rem',
              top: floatingText.animate ? '-60px' : '20px',
              opacity: floatingText.animate ? 0 : 1,
              transition: 'top 1s ease-out, opacity 1s ease-out',
              textShadow: floatingText.isHeal
                ? '0 0 10px rgba(0,0,0,0.8), 0 0 20px rgba(74,222,128,0.5)'
                : '0 0 10px rgba(0,0,0,0.8), 0 0 20px rgba(239,68,68,0.5)',
            }}
          >
            {floatingText.isHeal ? '+' : '-'}
            {floatingText.value}
          </span>
        )}

        {/* Nome eroe */}
        <div
          className="text-center mb-8"
          style={{ textShadow: `0 0 30px ${barColor}80, 0 4px 8px rgba(0,0,0,0.8)` }}
        >
          <span className="text-7xl font-bold text-white">{mode.heroName}</span>
        </div>

        {/* Barra HP */}
        <div className="relative h-14 bg-gray-800 rounded-full overflow-hidden border-2 border-gray-600">
          <div
            className="h-full rounded-full"
            style={{
              width: `${hpPercent}%`,
              backgroundColor: barColor,
              transition: 'width 0.5s ease-out',
              boxShadow: `0 0 20px ${barColor}60`,
            }}
          />
          <span
            className="absolute inset-0 flex items-center justify-center text-white font-bold text-2xl"
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
          >
            {currentHP} / {maxHP}
          </span>
        </div>
      </div>
    </div>
  );
}
